import { useEffect, type ReactNode } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useSiteContext } from './site-context'
import { useAppRuntime } from './app-runtime'
import { useI18n } from './i18n'
import { isCustomerPath, resolveCustomerRoute } from './canonical-customer-url'
import { BookingProvider } from '../features/booking/booking-state'

/** Do not mount checkout/auth effects until this browser is on the session origin. */
export function CustomerOriginGate({ children }: { children: ReactNode }) {
  const location = useRouterState({ select: (state) => state.location })
  const runtime = useAppRuntime()
  const { customerAppOrigin, site, isLoading } = useSiteContext()
  const { locale, l } = useI18n()
  const decision = resolveCustomerRoute({
    customerAppOrigin, currentOrigin: runtime.origin, pathname: location.pathname,
    search: location.searchStr, defaultLocale: site?.defaultLocale ?? locale,
  })
  const destination = decision.kind === 'redirect' ? decision.location : null

  useEffect(() => {
    if (destination) window.location.replace(destination)
  }, [destination])

  if (!isCustomerPath(location.pathname)) return children
  if (decision.kind === 'ready') return <BookingProvider>{children}</BookingProvider>
  if (isLoading || destination) return <div className="route-loading" role="status" aria-label={l('Buchung wird geöffnet', 'Opening booking', 'جارٍ فتح الحجز', 'Boeking openen')}><div className="route-loading__spinner" aria-hidden="true" /></div>
  return <main className="mx-auto max-w-lg p-8 text-center" role="alert">
    <h1 className="text-xl font-bold">{l('Die Buchungsseite ist vorübergehend nicht verfügbar.', 'Booking is temporarily unavailable.', 'الحجز غير متاح مؤقتًا.', 'Boeken is tijdelijk niet beschikbaar.')}</h1>
    <p className="mt-3">{l('Bitte lade die Seite erneut.', 'Please reload the page to try again.', 'أعد تحميل الصفحة للمحاولة مرة أخرى.', 'Laad de pagina opnieuw om het nogmaals te proberen.')}</p>
    <button className="mt-5 min-h-11 rounded-md border border-ink px-5" type="button" onClick={() => window.location.reload()}>{l('Erneut versuchen', 'Try again', 'إعادة المحاولة', 'Opnieuw proberen')}</button>
  </main>
}
